"use client";

import defaultAvatar from "@/assets/global/default-avatar.webp";
import { LikeIcon, ReplyIcon } from "@/components/platform/comment/icons";
import { SmartImage } from "@/components/ui/smart-image";
import { getBetTypeDisplay } from "@/constants/bet-type-style";
import { cn } from "@/lib/utils";
import { useUserInfo } from "@/store/user-info";
import { navigateToEventDetail } from "@/utils/router";
import { calc, get } from "@/utils/utils";
import { ArrowDownRight, ArrowUpRight, ChevronDown, ChevronUp } from "lucide-react";
import { useRouter } from "next/navigation";
import { useCommentItem } from "./hooks";
import type { CommentData } from "./types";

interface RootControlProps {
    totalReplies: number;
    isExpanded: boolean;
    onToggle: () => void;
}

interface CommentItemProps {
    comment: CommentData;
    replyParentId: string;
    isReply?: boolean;
    rootControls?: RootControlProps;
    matchups?: any[];
    showEventInfo?: boolean;
    showBuyButton?: boolean;
    onLike?: () => void;
    viewerUserId?: string | number | null; // 当前查看的个人中心用户ID
}

export default function CommentItem(
    {
        comment,
        replyParentId,
        isReply = false,
        rootControls,
        matchups,
        showEventInfo = false,
        showBuyButton = false,
        onLike,
        viewerUserId,
    }: CommentItemProps,
) {
    const router = useRouter();
    const currentUserId = useUserInfo( ( state ) => state.userId );
    const {
        isSelected,
        isLongContent,
        displayContent,
        isExpanded,
        isLiked,
        handleCommentClick,
        handleReplyClick,
        handleLike,
        toggleExpanded,
    } = useCommentItem( comment, replyParentId, onLike );

    const betTypeDisplay = getBetTypeDisplay( comment.betType, matchups );
    const baseLabel = comment.guessTitle ? `${betTypeDisplay.label} · ${comment.guessTitle}` : betTypeDisplay.label;
    const groupOrCategoryName = comment.groupTitle || comment.categoryName;
    const positionLabel = groupOrCategoryName ? `${groupOrCategoryName} · ${baseLabel}` : baseLabel;

    const replyCount = comment.comments ?? comment.replies?.length ?? 0;

    const navigateToProfile = ( targetUserId: string ) => {
        if ( !targetUserId ) return;

        // 如果提供了viewerUserId（在profile页面中），需要判断targetUserId是否等于viewerUserId
        // 如果targetUserId等于viewerUserId，则不跳转（已经在查看该用户的个人中心）
        if ( viewerUserId != null && String( targetUserId ) === String( viewerUserId ) ) {
            return;
        }

        // 如果targetUserId等于currentUserId，跳转到自己的个人中心
        if ( String( targetUserId ) === String( currentUserId ?? "" ) ) {
            router.push( "/profile" );
            return;
        }

        // 否则跳转到对应用户的个人中心
        router.push( `/profile?userId=${targetUserId}` );
    };

    const handleUserProfileClick = ( event: React.MouseEvent<HTMLButtonElement>, targetUserPath: string ) => {
        event.stopPropagation();
        navigateToProfile( targetUserPath );
    };

    return (
        <div
            id={`comment-anchor-${comment.serverId}`}
            onClick={handleCommentClick}
            className={cn(
                "flex cursor-pointer gap-3",
                isReply && "ml-14",
                isSelected && "bg-surface-muted px-3 -my-3 py-3",
                isReply && isSelected && "ml-16 -mr-3",
                !isReply && isSelected && "-mx-3",
            )}
        >
            <div className="shrink-0">
                <button
                    type="button"
                    onClick={( event ) => handleUserProfileClick( event, get( comment, "userId", "" ) )}
                    className={cn(
                        "bg-surface-disabled overflow-hidden",
                        isReply ? "size-8 rounded-[6.4px]" : "size-10 rounded-lg",
                    )}
                >
                    <SmartImage
                        src={comment.userAvatar || defaultAvatar}
                        alt={comment.userName}
                        className="w-full h-full object-cover"
                        imageUrlOptions={{ width: 150, height: 150 }}
                    />
                </button>
            </div>

            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex flex-1 items-center gap-1 min-w-0">
                        <button
                            type="button"
                            className="text-left font-semibold text-t1 max-w-64 truncate block hover:underline focus-visible:outline-none text-sm leading-[17px]"
                            onClick={( event ) => handleUserProfileClick( event, get( comment, "userId", "" ) )}
                        >
                            {comment.userName}
                        </button>
                        {isReply && comment.replyToUserName && (
                            <span className="text-sm text-t1 font-semibold flex items-center gap-1 min-w-0">
                                <span>‣</span>
                                <button
                                    type="button"
                                    className="text-left text-t1 font-medium text-xs max-w-48 truncate block hover:underline focus-visible:outline-none"
                                    onClick={( event ) =>
                                        handleUserProfileClick( event, get( comment, "replyToUserId", "" ) )}
                                >
                                    @{comment.replyToUserName}
                                </button>
                            </span>
                        )}
                    </div>
                    <span className="text-xs text-t2 shrink-0">{comment.time}</span>
                </div>

                {!isReply && !showEventInfo && (
                    <div className="mb-2 flex flex-col items-start justify-start">
                        <div className="max-w-[200px] overflow-hidden whitespace-nowrap text-ellipsis">
                            <span
                                className={cn(
                                    "inline-flex h-5 max-w-full items-center rounded-sm px-1.5 py-0 text-xs font-medium leading-none",
                                    betTypeDisplay.isNeutral ? "text-t2 bg-overlay-subtle" : betTypeDisplay.badgeClass,
                                )}
                            >
                                {positionLabel}
                            </span>
                        </div>
                    </div>
                )}

                <div className="mb-3">
                    <p className="text-t1 leading-normal whitespace-pre-wrap break-all text-sm font-normal">
                        {displayContent}
                        {isLongContent && (
                            <button
                                type="button"
                                onClick={( e ) => {
                                    e.stopPropagation();
                                    toggleExpanded();
                                }}
                                className="ml-2 inline-flex items-center gap-1 font-medium capitalize text-xs text-t2"
                            >
                                {isExpanded ? "Collapse" : "Expand"}
                                {isExpanded ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
                            </button>
                        )}
                    </p>
                </div>

                {!isReply && showEventInfo && comment.eventTitle && (
                    <div className="mb-3">
                        <h4
                            onClick={( e ) => {
                                e.stopPropagation();
                                if ( comment.eventNo ) {
                                    void navigateToEventDetail( router, {
                                        eventNo: comment.eventNo,
                                        commentId: comment.serverId,
                                    } );
                                }
                            }}
                            className={cn(
                                "text-sm font-semibold mb-2 cursor-pointer hover:opacity-70 transition-opacity leading-normal",
                                "text-data-primary",
                            )}
                        >
                            {comment.eventTitle}
                        </h4>

                        {( () => {
                            const userChancePercentage = get( comment, "userChancePercentage", 0 );
                            const nowChancePercentage = get( comment, "nowChancePercentage", 0 );
                            const probabilityChange = calc( "nowChancePercentage - userChancePercentage", {
                                nowChancePercentage,
                                userChancePercentage,
                            } );
                            const probabilityChangeValue = typeof probabilityChange === "number"
                                ? probabilityChange
                                : Number( probabilityChange || 0 );
                            const userChancePercentStr = calc( "value / 10 | <=1, %", { value: userChancePercentage } );
                            const nowChancePercentStr = calc( "value / 10 | <=1, %", { value: nowChancePercentage } );
                            const guessTitleText = get( comment, "guessTitle", "" );
                            const groupOrCategoryName = get( comment, "groupTitle | categoryName", "" );
                            const positionSegments: Array<{ text: string; isFirst: boolean }> = [];
                            positionSegments.push( { text: betTypeDisplay.label, isFirst: true } );
                            if ( groupOrCategoryName ) {
                                positionSegments.push( { text: groupOrCategoryName, isFirst: false } );
                            }
                            if ( guessTitleText ) {
                                positionSegments.push( { text: guessTitleText, isFirst: false } );
                            }
                            if ( !betTypeDisplay.isNeutral ) {
                                positionSegments.push( { text: `${userChancePercentStr} chance`, isFirst: false } );
                            }
                            const betTypeTextClass = betTypeDisplay.isNeutral ? "text-t1" : betTypeDisplay.textClass;

                            const isChanceEqual = userChancePercentage === nowChancePercentage;
                            return (
                                <>
                                    <div
                                        onClick={( e ) => {
                                            e.stopPropagation();
                                            if ( comment.eventNo ) {
                                                void navigateToEventDetail( router, {
                                                    eventNo: comment.eventNo,
                                                    commentId: comment.serverId,
                                                } );
                                            }
                                        }}
                                        className="leading-normal text-xs cursor-pointer hover:opacity-70 transition-opacity"
                                    >
                                        {positionSegments.map( ( segment, index ) => (
                                            <span key={index}>
                                                {index > 0 && <span className="text-data-primary">·</span>}
                                                <span
                                                    className={segment.isFirst ? betTypeTextClass : "text-data-primary"}
                                                >
                                                    {segment.text}
                                                </span>
                                            </span>
                                        ) )}{" "}
                                        {!betTypeDisplay.isNeutral && probabilityChangeValue > 0 && (
                                            <ArrowUpRight className="w-3.5 h-3.5 text-state-success inline-block" />
                                        )}
                                        {!betTypeDisplay.isNeutral && probabilityChangeValue < 0 && (
                                            <ArrowDownRight className="w-3.5 h-3.5 text-state-danger inline-block" />
                                        )}
                                        {!betTypeDisplay.isNeutral && (
                                            <>
                                                {" "}
                                                {isChanceEqual && <span className="inline-block mx-1">—</span>}
                                                <span className="text-data-primary">
                                                    Now {nowChancePercentStr} chance
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </>
                            );
                        } )()}
                    </div>
                )}

                <div className="mt-2 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={( e ) => {
                                e.stopPropagation();
                                handleReplyClick();
                            }}
                            className="flex items-center gap-1 text-t2 transition-colors hover:text-t1"
                        >
                            <ReplyIcon className="size-6" />
                            {replyCount > 0 && <span className="text-xs font-medium text-t2">{replyCount}</span>}
                        </button>

                        <button
                            onClick={( e ) => {
                                e.stopPropagation();
                                handleLike();
                            }}
                            className="flex items-center gap-1 transition-colors"
                        >
                            <LikeIcon liked={isLiked} className="size-6 text-t2 hover:text-t1" />
                            {comment.likes > 0 && (
                                <span
                                    className={cn( "text-xs font-medium", isLiked ? "text-state-danger" : "text-t2" )}
                                >
                                    {comment.likes}
                                </span>
                            )}
                        </button>
                    </div>

                    {showBuyButton && comment.eventNo && (
                        <button
                            onClick={( e ) => {
                                e.stopPropagation();
                                void navigateToEventDetail( router, {
                                    eventNo: comment.eventNo!,
                                    guessNo: comment.guessNo,
                                    guessType: comment.betType,
                                } );
                            }}
                            className="bg-action-primary text-content-fixed-inverse font-medium hover:bg-overlay-strong transition-colors min-h-8 min-w-[50px] shrink-0 px-3 py-1.5 rounded-lg text-xs leading-normal"
                        >
                            Buy
                        </button>
                    )}
                </div>
                {rootControls && (
                    <button
                        type="button"
                        onClick={( e ) => {
                            e.stopPropagation();
                            rootControls.onToggle();
                        }}
                        className="mt-3 text-xs text-t2"
                    >
                        {rootControls.isExpanded
                            ? `Hide ${rootControls.totalReplies} Replies`
                            : `Show ${rootControls.totalReplies} Replies`}
                    </button>
                )}
            </div>
        </div>
    );
}
